import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ChefHat } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/constants/brand";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    let active = true;
    const poll = async () => {
      if (!active) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch {}
      if (attempts >= 12) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, refresh]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4">
      <div className="w-full max-w-md rounded-3xl border border-espresso/10 bg-white p-9 text-center shadow-[0_8px_30px_rgba(44,30,22,0.08)]" data-testid="payment-success-page">
        <img src={BRAND.logo} alt="" className="mx-auto mb-4 h-20 w-20 rounded-full object-contain" />
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-loretta-red" />
            <h1 className="font-serif text-2xl font-bold text-espresso">Setting your table…</h1>
            <p className="mt-2 text-espressolt">Confirming your Premium membership.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-loretta-olive" />
            <h1 className="font-serif text-3xl font-bold text-espresso">Benvenuto to Premium!</h1>
            <p className="mt-2 text-espressolt">Loretta will now remember your preferences, pantry, favorites and more.</p>
            <button onClick={() => navigate("/")} data-testid="go-to-kitchen-button" className="mt-6 w-full rounded-full bg-loretta-red py-3.5 font-semibold text-white hover:bg-loretta-redhover">
              Back to the Kitchen
            </button>
          </>
        )}
        {(status === "failed" || status === "error" || status === "timeout") && (
          <>
            <XCircle className="mx-auto mb-4 h-12 w-12 text-loretta-red" />
            <h1 className="font-serif text-2xl font-bold text-espresso">Hmm, something's off</h1>
            <p className="mt-2 text-espressolt">{status === "timeout" ? "Still processing — check back in a minute." : "We couldn't confirm your payment."}</p>
            <Link to="/" className="mt-6 inline-block w-full rounded-full border border-espresso/15 py-3.5 font-semibold text-espresso hover:bg-creamalt">Back to the Kitchen</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4">
      <div className="w-full max-w-md rounded-3xl border border-espresso/10 bg-white p-9 text-center shadow-[0_8px_30px_rgba(44,30,22,0.08)]" data-testid="payment-cancel-page">
        <ChefHat className="mx-auto mb-4 h-12 w-12 text-espressolt" />
        <h1 className="font-serif text-2xl font-bold text-espresso">No worries, tesoro</h1>
        <p className="mt-2 text-espressolt">Checkout was cancelled. Free chat is always here whenever you're hungry.</p>
        <Link to="/" className="mt-6 inline-block w-full rounded-full bg-loretta-red py-3.5 font-semibold text-white hover:bg-loretta-redhover">Back to the Kitchen</Link>
      </div>
    </div>
  );
}
